import { toBdPoint } from '../geometry';

/**
 * 弹窗覆盖物工厂
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 创建锚点覆盖物（PopupOverlay），用于弹窗定位与地图联动
 * 2. 提供弹窗的 open / close / setPosition 操作
 *
 * 设计要点：
 * - PopupOverlay class 定义在顶层，不在工厂函数内部，避免每次 createPopup 都创建新类
 * - 通过 BMapGL 守卫确保 SDK 就绪后再创建覆盖物
 */

/** 获取 BMapGL.Overlay 基类，未就绪时返回 null */
function getOverlayBase(): any | null {
  return window?.BMapGL?.Overlay ?? null;
}

/**
 * 弹窗锚点覆盖物
 *
 * 在地图 floatPane 内放置一个 0×0 的锚点 div，利用 draw 回调获取实时屏幕坐标，
 * 把外部面板定位到锚点上方，实现「随地图联动 + 正常文字选择」的效果。
 *
 * 顶层 class 定义，避免子应用加载时 BMapGL 未就绪导致崩溃。
 */
class PopupOverlay {
  private overlayRef: any = null;
  private overlayMap: any = null;
  private mounted = false;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private positionFrameId: number | null = null;

  constructor(
    private anchor: HTMLElement,
    private panel: HTMLElement,
    private point: any
  ) {}

  /** 创建原生 BMapGL.Overlay 实例（必须在 BMapGL 就绪后调用） */
  create(): any {
    const Base = getOverlayBase();
    if (!Base) {
      console.error('[PopupOverlay] BMapGL.Overlay is not available');
      return null;
    }

    // 保存到闭包以便内部 class 访问
    const self = this;

    class InnerOverlay extends Base {
      initialize = (map: any) => {
        self.overlayRef = this;
        self.overlayMap = map;
        map.getPanes().floatPane.appendChild(self.anchor);
        self.mounted = true;
        self.observePanelChanges();
        return self.anchor;
      };

      draw = () => {
        if (!self.mounted || !self.overlayMap) return;
        const pixel = self.overlayMap.pointToOverlayPixel(self.point);
        self.anchor.style.left = `${pixel.x}px`;
        self.anchor.style.top = `${pixel.y}px`;
        self.positionPanel();
      };
    }

    return new InnerOverlay();
  }

  positionPanel() {
    const rect = this.anchor.getBoundingClientRect();
    this.panel.style.left = `${rect.left - 50}px`;
    this.panel.style.top = `${rect.top - this.panel.offsetHeight - 12}px`;
  }

  private schedulePositionPanel = () => {
    if (this.positionFrameId !== null) return;
    this.positionFrameId = requestAnimationFrame(() => {
      this.positionFrameId = null;
      if (!this.mounted || !this.overlayMap) return;
      this.overlayRef?.draw?.();
    });
  };

  /**
   * React Portal 内容会在弹窗 DOM 创建后异步挂载。
   * 首次 draw 时 panel.offsetHeight 可能还是 0，导致详情面板定位异常，看起来像空面板。
   * 监听内容/尺寸变化后重新 draw，确保字段渲染完成后按真实高度定位。
   */
  private observePanelChanges() {
    this.stopObservePanelChanges();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.schedulePositionPanel);
      this.resizeObserver.observe(this.panel);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(this.schedulePositionPanel);
      this.mutationObserver.observe(this.panel, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  stopObservePanelChanges() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    if (this.positionFrameId !== null) {
      cancelAnimationFrame(this.positionFrameId);
      this.positionFrameId = null;
    }
  }

  updatePoint(nextPoint: any) {
    this.point = toBdPoint(nextPoint);
    this.overlayRef?.draw?.();
  }

  isMounted() {
    return this.mounted;
  }

  getOverlayRef() {
    return this.overlayRef;
  }

  setMounted(val: boolean) {
    this.mounted = val;
  }

  setOverlayMap(val: any) {
    this.overlayMap = val;
  }
}

/**
 * 弹窗管理器
 *
 * 管理所有弹窗覆盖物的生命周期
 */
export class PopupManager {
  private popupOverlays: Array<{
    __isPopupOverlay: boolean;
    id: string | number;
    element: HTMLElement;
    open: () => void;
    close: () => void;
    setPosition: (coords: [number, number]) => void;
  }> = [];

  constructor(private map: any) {}

  createPopup = (
    id: string | number,
    element: HTMLElement,
    coordinates: [number, number]
  ) => {
    const point = toBdPoint(coordinates);

    const anchor = document.createElement('div');
    anchor.style.cssText = 'position:absolute;width:0;height:0;';

    const popupOverlay = new PopupOverlay(anchor, element, point);
    const overlay = popupOverlay.create();

    return {
      __isPopupOverlay: true,
      id,
      element,
      open: () => {
        element.style.position = 'fixed';
        // 与 views/MapEditor/utils/zIndex.ts 的 MAP_EDITOR_DETAIL_POPUP_Z_INDEX 保持一致
        element.style.zIndex = '950';
        element.style.pointerEvents = 'auto';
        this.map.addOverlay(overlay);
        requestAnimationFrame(() => popupOverlay.getOverlayRef()?.draw?.());
      },
      close: () => {
        if (!popupOverlay.isMounted()) return;
        popupOverlay.stopObservePanelChanges();
        this.map.removeOverlay(popupOverlay.getOverlayRef() ?? overlay);
        anchor.remove();
        element.remove();
        popupOverlay.setMounted(false);
        popupOverlay.setOverlayMap(null);
      },
      setPosition: (nextCoordinates: [number, number]) => {
        popupOverlay.updatePoint(nextCoordinates);
      },
    };
  };

  addOverlay(element: any) {
    if (element?.__isPopupOverlay) {
      this.popupOverlays.push(element);
      element.open?.();
      return;
    }
    this.map.addOverlay(element);
  }

  /**
   * 创建弹窗 DOM、拦截事件冒泡、挂载 overlay 并返回 mountNode
   */
  updatePopupPosition(id: string | number, coordinates: [number, number]) {
    const popup = this.popupOverlays.find(
      (overlay) => overlay.id === id
    );
    popup?.setPosition(coordinates);
  }

  showPopupForData(
    id: string | number,
    coordinates: [number, number],
    overlayRoot: HTMLElement
  ): { mountNode: HTMLElement } {
    const popNode = document.createElement('div');
    popNode.setAttribute('id', String(id));
    popNode.className = 'ol-popup';
    [
      'mousedown',
      'mouseup',
      'click',
      'dblclick',
      'mousemove',
      'wheel',
      'contextmenu',
    ].forEach((eventType) => {
      popNode.addEventListener(eventType, (e) => e.stopPropagation());
    });
    (overlayRoot || document.body).appendChild(popNode);
    const popup = this.createPopup(id, popNode, coordinates);
    this.addOverlay(popup);
    popup.setPosition(coordinates);
    return { mountNode: popNode };
  }

  clearAll() {
    this.popupOverlays.forEach((overlay) => overlay.close?.());
    this.popupOverlays = [];
  }
}
